import { CaretRightOutlined, CheckOutlined, InfoCircleOutlined, UploadOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Col, Collapse, Divider, Form, Input, Row, Select, Space, Switch, Table, Tag, Tooltip, Typography, Upload, message } from "antd";
import { ALLOWED_EXTENSIONS, ALLOWED_TYPES, formItemLayout, MAX_FILE_SIZE_MB, rpaTemplateTypesCode, selectOptionsTypes } from "../../../utils/constants";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DynamicSelect from "../../../components/common/dynamicSelect";
const { Text } = Typography;

export const MappingSummary = ({ excelFields, mappings }) => {
    const { t } = useTranslation();

    return (
        <Collapse
            size="small"
            collapsible="icon"
            defaultActiveKey={['1']}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            items={[
                {
                    key: '1',
                    label: t('template.fieldMappingSummary'),
                    children: (
                        <Space direction="vertical">
                            {excelFields.map((exField) => {
                                const mapped = mappings[exField.label];
                                const value = mapped?.value;

                                if (!value) {
                                    return (
                                        <Text style={{ color: "#d68e00" }} key={exField.label}>
                                            <WarningOutlined /> {exField.label} → {t('template.notMapped')}
                                        </Text>
                                    );
                                } else {
                                    return (
                                        <Text key={exField.label}>
                                            <CheckOutlined style={{ color: '#25a209' }} /> {exField.label} → <strong>{value.description}</strong>{' '}
                                            {mapped.auto && <Tag color="blue">{t('template.automapped')}</Tag>}
                                        </Text>
                                    );
                                }
                            })}
                        </Space>
                    ),
                },
            ]}
        />
    )
}

export const TemplateFormDetails = ({ form, submitTemplateForm, loadingFieldsMapping, formLocation, rpaTemplateTypes, templateTypes }) => {
    const { t } = useTranslation();
    const salesOrganization = useSelector((state) => state.utils.salesOrganization);

    const normFile = (e) => {
        // Normalize file event to file list
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const beforeUpload = (file) => {
        const isExcelType = ALLOWED_TYPES.includes(file.type);
        const isExcelExt = ALLOWED_EXTENSIONS.some(ext =>
            file.name.toLowerCase().endsWith(ext)
        );

        if (!isExcelType && !isExcelExt) {
            message.error(t('messages.uploadTypeAllowed'));
            return Upload.LIST_IGNORE;
        }

        const isLt50MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
        if (!isLt50MB) {
            message.error(t('messages.uploadSizeLimit'));
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const handelFormItemsLabels = (type, selectedOption) => {        
        const fieldName = `${type}Name`;
        form.setFieldsValue({
            [fieldName]: selectedOption.label
        });

    }

    const defaultRpaTemplateType = rpaTemplateTypes.find((type) => type.code === rpaTemplateTypesCode.rpaCS);
    if (defaultRpaTemplateType) {       
        form.setFieldsValue({
            rPATemplateType: defaultRpaTemplateType.id,
        });
       
    }

    return (
        <Form
            id="newTemplateDetails"
            layout="vertical"
            form={form}
            {...formItemLayout}
            onFinish={formLocation === 'new' && submitTemplateForm}
            size="small"
        >
            <Row gutter={16}>

                <Form.Item name="soldToName" hidden><Input type="hidden" /></Form.Item>
                <Form.Item name="shipToName" hidden><Input type="hidden" /></Form.Item>
                <Form.Item name="salesOrganizationName" hidden><Input type="hidden" /></Form.Item>
                <Form.Item name="orderTypeName" hidden><Input type="hidden" /></Form.Item>
                <Form.Item name="purchasedOrderTypeName" hidden><Input type="hidden" /></Form.Item>
                <Form.Item name="distributionChannelName" hidden><Input type="hidden" /></Form.Item>


                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('template.templateName')}
                        name="templateName"
                        rules={[{ required: true, message: t('messages.requiredField') }]}                       
                    >
                        <Input />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6     }}>
                    <Form.Item
                        label={t('order.soldTo')}
                        name="soldToParty"
                        rules={[{ required: true, message: t('messages.requiredField') }]}                      
                    >
                        <DynamicSelect type={selectOptionsTypes.soldTo} onSelectChange={handelFormItemsLabels} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('order.shipTo')}
                        name="shipToParty"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <DynamicSelect type={selectOptionsTypes.shipTo} onSelectChange={handelFormItemsLabels} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('order.salesOrganization')}
                        name="salesOrganization"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <DynamicSelect type={selectOptionsTypes.salesOrganization} onSelectChange={handelFormItemsLabels} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('order.orderType')}
                        name="orderType"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <DynamicSelect type={selectOptionsTypes.orderType} onSelectChange={handelFormItemsLabels} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('order.purchaseOrderType')}
                        name="purchasedOrderType"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <DynamicSelect type={selectOptionsTypes.purchasedOrderType} onSelectChange={handelFormItemsLabels} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('order.distributionChannel')}
                        name="distributionChannel"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <DynamicSelect type={selectOptionsTypes.distributionChannel} onSelectChange={handelFormItemsLabels} />
                    </Form.Item>
                </Col>
 
               <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('template.rpaTemplateType')}
                        name="rPATemplateType"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <Select placeholder={t('general.select')} options={rpaTemplateTypes.map((type) => ({ value: type.id, label: type.description }))}  onChange={handelFormItemsLabels}/>
                    </Form.Item>
                </Col> 

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <Form.Item
                        label={t('template.inputTemplateType')}
                        name="inputTemplateTypeID"
                        rules={[{ required: true, message: t('messages.requiredField') }]}
                    >
                        <Select placeholder={t('general.select')} options={templateTypes.map((type) => ({ value: type.id, label: type.description }))}  onChange={handelFormItemsLabels}/>
                    </Form.Item>
                    </Col>

                {formLocation === 'new' && (
                    <>
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('general.file')}
                                name="file"
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Upload
                                    accept=".xls,.xlsx,.csv"
                                    maxCount={1}
                                    beforeUpload={beforeUpload}
                                    showUploadList={true}
                                    customRequest={({ file, onSuccess }) => {
                                        setTimeout(() => {
                                            onSuccess("ok");
                                        }, 500);
                                    }}
                                >
                                    <Button icon={<UploadOutlined />}>{t('order.selectFile')}</Button>
                                </Upload>
                            </Form.Item>
                        </Col>

                        <Divider />

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item label={null}>
                                <Button type="dashed" htmlType="submit" block loading={loadingFieldsMapping}>
                                    {t('template.startMapping')}
                                </Button>
                            </Form.Item>
                        </Col>
                    </>
                )}
            </Row>
        </Form>
    )
}

export const getAvailableOptions = (currentField, mappings, excelFieldsList, dbFieldsList) => {

    if (!Array.isArray(dbFieldsList)) return [];
    const usedValues = Object.values(mappings)
        .filter(v => v?.value)
        .map(v => v.value?.name);
    return dbFieldsList.filter(f => !usedValues.includes(f.name))
        .map(f => ({ value: f.name, label: f.description }));
};

export const mappingsColumns = (t, mappings, excelFields, handleSelectChange, disableFields, dbFields) => {
    const columns = [
        {
            title: `${t('template.sourceField')} (${t('template.excelFile')})`,
            dataIndex: 'excelField',
            key: 'excelField',
            width: '50%'
        },
        {
            title: `${t('template.destinationField')} (${t('template.database')})`,
            dataIndex: 'dbField',
            key: 'dbField',
            render: (_, record) => {
                const current = mappings[record.excelField];
                return (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Select
                            style={{ flex: 1, width: "230px" }}
                            placeholder={t('template.selectExcelField')}
                            value={current?.value?.description}
                            onChange={(val) => {
                                const sel = dbFields.find(f => f.name === val);
                                handleSelectChange(record.excelField, sel, 'db');
                            }}
                            options={getAvailableOptions(record.excelField, mappings, excelFields, dbFields)}
                            allowClear
                            showSearch
                            disabled={disableFields}
                            size="small"
                            optionFilterProp="label"
                        />
                        {current?.auto && current.value && (
                            <Tag color="blue">
                                <Tooltip title={t('template.automapped')}><InfoCircleOutlined /></Tooltip>
                            </Tag>
                        )}
                    </div>
                );
            }
        },
        {
            title: `${t('general.required')}`,
            dataIndex: 'isRequired',
            key: 'isRequired',
            width: '20%',
            render: (_, record) => {
                const current = mappings[record.excelField];
                const hasDbValue = !!current?.value;
                return (
                    <Switch
                        checked={!!current?.isRequired}
                        onChange={(val) => {
                            handleSelectChange(record.excelField, val, 'isRequired');
                        }}
                        disabled={!hasDbValue || disableFields}
                    />
                );
            }
        },
    ];

    return columns;
}

export const templateMappingsDataSource = (dbFields, excelFields) =>
    excelFields.map((field, idx) => ({ key: idx, excelField: field.label }));

export const TableMappings = ({ dbFields, mappings, excelFields, handleSelectChange, disableFields }) => {
    const { t } = useTranslation();
    return (
        <Collapse
            size="small"
            defaultActiveKey={['1']}
            collapsible="icon"
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            items={[
                {
                    key: '1',
                    label: t('template.fieldMapping'),
                    children: (
                        <Table
                            size='small'
                            dataSource={templateMappingsDataSource(dbFields, excelFields)}
                            columns={mappingsColumns(t, mappings, excelFields, handleSelectChange, disableFields, dbFields)}
                            pagination={false}
                            bordered
                        />
                    ),
                },
            ]}
        />
    )
}